import Phaser from 'phaser';
import { getSettingsSnapshot } from '@/state/settingsStore';

// Texture keys cho effects (procedural — generate ở PreloadScene)
export const FX = {
  slash: 'fx_slash',
  whirlwind: 'fx_whirlwind',
  shield: 'fx_shield',
  fireball: 'fx_fireball',
  storm: 'fx_storm',
  manaShield: 'fx_mana_shield',
  arrow: 'fx_arrow',
  arrowRain: 'fx_arrow_rain',
  heal: 'fx_heal',
  spark: 'fx_spark',
  basicMelee: 'fx_basic_melee',
} as const;

// Use additive blending where supported for glow-y look.
function additive(obj: Phaser.GameObjects.GameObject) {
  // Phaser v3 supports BlendModes.ADD — guard for headless/test envs.
  try {
    (obj as any).setBlendMode?.(Phaser.BlendModes.ADD);
  } catch {
    /* ignore */
  }
}

// Soft camera shake (intensity small, gameplay-friendly).
function shake(scene: Phaser.Scene, ms = 120, intensity = 0.004) {
  scene.cameras.main?.shake(ms, intensity, false);
}

// Quick screen flash (subtle). Uses an overlay rectangle so we can control alpha precisely.
function flash(scene: Phaser.Scene, color = 0xffffff, ms = 90, alpha = 0.15) {
  const cam = scene.cameras.main;
  if (!cam) return;
  const rect = scene.add.rectangle(
    cam.midPoint.x, cam.midPoint.y,
    cam.width / cam.zoom + 200, cam.height / cam.zoom + 200,
    color, alpha,
  ).setDepth(60).setScrollFactor(0);
  additive(rect);
  scene.tweens.add({
    targets: rect, alpha: 0, duration: ms,
    onComplete: () => rect.destroy(),
  });
}

// ----- Damage / heal popup -----
export function spawnDamageNumber(
  scene: Phaser.Scene,
  x: number, y: number,
  amount: number,
  opts: { crit?: boolean; heal?: boolean } = {},
) {
  if (amount <= 0) return;
  if (!getSettingsSnapshot().showDamageNumbers) return;
  const color = opts.heal ? '#7CFC8B' : opts.crit ? '#FFD166' : '#FFFFFF';
  const fontSize = opts.crit ? '26px' : '16px';
  const prefix = opts.heal ? '+' : '';
  const suffix = opts.crit ? '!' : '';
  const label = scene.add.text(x + Phaser.Math.Between(-6, 6), y - 10, `${prefix}${amount}${suffix}`, {
    fontFamily: '"Philosopher", "Marcellus SC", Impact, serif', fontSize, color, stroke: '#000', strokeThickness: 4,
  }).setOrigin(0.5).setDepth(50);
  scene.tweens.add({
    targets: label, y: y - 60, alpha: { from: 1, to: 0 },
    scale: opts.crit ? { from: 1.6, to: 1 } : { from: 1.1, to: 1 },
    duration: 800, ease: 'Cubic.easeOut',
    onComplete: () => label.destroy(),
  });
}

// ----- Generic small impact ring -----
function spawnImpactRing(
  scene: Phaser.Scene,
  x: number, y: number,
  color: number,
  radius = 30,
  opts: { width?: number; duration?: number; add?: boolean } = {},
) {
  const width = opts.width ?? 3;
  const dur = opts.duration ?? 320;
  const g = scene.add.graphics({ x, y }).setDepth(22);
  if (opts.add) additive(g);
  g.lineStyle(width, color, 1).strokeCircle(0, 0, 6);
  scene.tweens.add({
    targets: g,
    duration: dur,
    onUpdate: (tw) => {
      const p = tw.progress;
      g.clear();
      g.lineStyle(width * (1 - p), color, 1 - p).strokeCircle(0, 0, 6 + radius * p);
    },
    onComplete: () => g.destroy(),
  });
}

// Filled translucent shockwave (great for explosions).
function spawnShockwave(
  scene: Phaser.Scene, x: number, y: number, color: number, radius = 60, duration = 380,
) {
  const g = scene.add.graphics({ x, y }).setDepth(21);
  additive(g);
  scene.tweens.add({
    targets: g,
    duration,
    onUpdate: (tw) => {
      const p = tw.progress;
      g.clear();
      g.fillStyle(color, 0.35 * (1 - p));
      g.fillCircle(0, 0, radius * p);
    },
    onComplete: () => g.destroy(),
  });
}

// ----- Particle burst (small, no atlas) -----
function burst(
  scene: Phaser.Scene, x: number, y: number,
  color: number, count = 8, speed = 60, size = 3,
  opts: { add?: boolean; gravity?: number; lifeMs?: number } = {},
) {
  const life = opts.lifeMs ?? 360;
  for (let i = 0; i < count; i++) {
    const ang = (Math.PI * 2 * i) / count + Math.random() * 0.4;
    const sp = speed * (0.7 + Math.random() * 0.7);
    const r = scene.add.rectangle(x, y, size, size, color).setDepth(23);
    if (opts.add) additive(r);
    const vx = Math.cos(ang) * sp;
    const vy = Math.sin(ang) * sp;
    const ms = life + Math.random() * 160;
    const gravity = opts.gravity ?? 0;
    scene.tweens.add({
      targets: r,
      x: x + vx,
      y: y + vy + gravity,
      alpha: 0,
      angle: Phaser.Math.Between(-180, 180),
      duration: ms,
      ease: 'Cubic.easeOut',
      onComplete: () => r.destroy(),
    });
  }
}

// Sparkle "stars" with cross-twinkle look (simple rotating sprite).
function sparkleStars(scene: Phaser.Scene, x: number, y: number, color: number, count = 6, radius = 36) {
  for (let i = 0; i < count; i++) {
    const a = (Math.PI * 2 * i) / count + Math.random();
    const ox = Math.cos(a) * radius * (0.6 + Math.random() * 0.4);
    const oy = Math.sin(a) * radius * (0.6 + Math.random() * 0.4);
    const s = scene.add.rectangle(x + ox, y + oy, 5, 5, color).setDepth(24).setAngle(45);
    additive(s);
    scene.tweens.add({
      targets: s, scale: { from: 0.2, to: 1.4 }, alpha: { from: 1, to: 0 },
      angle: 135, duration: 500 + Math.random() * 200, ease: 'Cubic.easeOut',
      onComplete: () => s.destroy(),
    });
  }
}

// Trailing afterimages of a sprite (for projectiles).
function emitTrail(
  scene: Phaser.Scene, src: Phaser.GameObjects.Sprite, color: number,
  intervalMs = 28, life = 260, scale = 1, alpha = 0.55,
): Phaser.Time.TimerEvent {
  return scene.time.addEvent({
    delay: intervalMs, loop: true,
    callback: () => {
      if (!src.active) return;
      const t = scene.add.sprite(src.x, src.y, src.texture.key)
        .setDepth(20).setRotation(src.rotation).setScale(src.scaleX * scale, src.scaleY * scale)
        .setTint(color).setAlpha(alpha);
      additive(t);
      scene.tweens.add({
        targets: t, alpha: 0, scale: src.scaleX * scale * 0.4,
        duration: life, ease: 'Cubic.easeOut',
        onComplete: () => t.destroy(),
      });
    },
  });
}

// ----- Basic attack (no skill) -----
export function spawnBasicAttackFx(
  scene: Phaser.Scene,
  klass: 'warrior' | 'wizard' | 'archer',
  from: { x: number; y: number },
  to: { x: number; y: number },
  onImpact?: () => void,
) {
  if (klass === 'warrior') {
    // ---- Warrior: arcing slash with anticipation pose + impact ----
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const mx = from.x + Math.cos(angle) * 30;
    const my = from.y + Math.sin(angle) * 30;

    // Anticipation flash at hilt position.
    const hilt = scene.add.circle(
      from.x + Math.cos(angle) * 14,
      from.y + Math.sin(angle) * 14,
      4, 0xfff3a0, 0.9,
    ).setDepth(20);
    additive(hilt);
    scene.tweens.add({
      targets: hilt, scale: { from: 0.5, to: 1.6 }, alpha: 0, duration: 140,
      onComplete: () => hilt.destroy(),
    });

    // Main slash arc — sweeps from -55deg to +55deg.
    const arc = scene.add.sprite(mx, my, FX.basicMelee).setDepth(21).setScale(0.5, 0.4);
    additive(arc);
    arc.setRotation(angle - Math.PI / 4);
    scene.tweens.add({
      targets: arc,
      scaleX: 1.5, scaleY: 1.1,
      rotation: angle + Math.PI / 4,
      alpha: { from: 1, to: 0 },
      duration: 240, ease: 'Cubic.easeOut',
      onComplete: () => arc.destroy(),
    });

    // Soft after-image arc (slightly delayed, larger, faded).
    const ghost = scene.add.sprite(mx, my, FX.basicMelee).setDepth(20).setScale(0.6, 0.5).setAlpha(0.5);
    additive(ghost);
    ghost.setRotation(angle - Math.PI / 4);
    scene.tweens.add({
      targets: ghost,
      scaleX: 1.7, scaleY: 1.2,
      rotation: angle + Math.PI / 4,
      alpha: 0,
      duration: 320, delay: 60, ease: 'Cubic.easeOut',
      onComplete: () => ghost.destroy(),
    });

    // Edge sparks streaking out along swing tangent.
    for (let i = 0; i < 5; i++) {
      const t = -0.4 + (i / 4) * 0.8; // -0.4 .. 0.4
      const a = angle + t * 0.9;
      const sx = from.x + Math.cos(a) * 36;
      const sy = from.y + Math.sin(a) * 36;
      const sp = scene.add.rectangle(sx, sy, 3, 3, 0xfff3a0, 1).setDepth(22);
      additive(sp);
      scene.tweens.add({
        targets: sp,
        x: sx + Math.cos(a) * 24,
        y: sy + Math.sin(a) * 24,
        alpha: 0,
        duration: 260, delay: i * 18, ease: 'Cubic.easeOut',
        onComplete: () => sp.destroy(),
      });
    }

    scene.time.delayedCall(130, () => {
      onImpact?.();
      spawnImpactRing(scene, to.x, to.y, 0xffffff, 24, { add: true, width: 3 });
      spawnImpactRing(scene, to.x, to.y, 0xffd180, 16, { add: true, width: 2, duration: 260 });
      spawnShockwave(scene, to.x, to.y, 0xfff3a0, 22, 260);
      burst(scene, to.x, to.y, 0xfff3a0, 8, 60, 3, { add: true });
      sparkleStars(scene, to.x, to.y, 0xffffff, 3, 18);
    });
    return;
  }

  if (klass === 'wizard') {
    // ---- Wizard: small magic bolt with charge-up + crackle trail ----
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const sx = from.x + Math.cos(angle) * 14;
    const sy = from.y + Math.sin(angle) * 14;

    // Charge-up flash at caster hand.
    const charge = scene.add.circle(sx, sy, 5, 0x6ec1ff, 1).setDepth(20);
    additive(charge);
    scene.tweens.add({
      targets: charge, scale: { from: 0.3, to: 1.6 }, alpha: 0, duration: 160,
      onComplete: () => charge.destroy(),
    });

    const proj = scene.add.sprite(sx, sy, FX.spark).setDepth(21).setScale(1.1);
    additive(proj);
    proj.setRotation(angle);
    // Outer glow halo following projectile.
    const halo = scene.add.sprite(sx, sy, FX.spark).setDepth(20).setScale(2.0).setAlpha(0.4).setTint(0x6ec1ff);
    additive(halo);

    const trail = emitTrail(scene, proj, 0x6ec1ff, 24, 240, 1.2, 0.55);
    // Crackle: small white sparks orbiting projectile.
    const crackle = scene.time.addEvent({
      delay: 40, loop: true, callback: () => {
        const ox = Phaser.Math.Between(-6, 6);
        const oy = Phaser.Math.Between(-6, 6);
        const c = scene.add.rectangle(proj.x + ox, proj.y + oy, 2, 2, 0xffffff, 1).setDepth(22);
        additive(c);
        scene.tweens.add({
          targets: c, alpha: 0, scale: 0.2, duration: 220,
          onComplete: () => c.destroy(),
        });
      },
    });

    const dist = Phaser.Math.Distance.Between(from.x, from.y, to.x, to.y);
    const dur = Math.min(420, Math.max(120, dist * 1.6));
    scene.tweens.add({
      targets: [proj, halo],
      x: to.x, y: to.y,
      duration: dur, ease: 'Linear',
      onComplete: () => {
        trail.remove();
        crackle.remove();
        proj.destroy();
        halo.destroy();
        onImpact?.();
        spawnShockwave(scene, to.x, to.y, 0x6ec1ff, 30, 320);
        spawnImpactRing(scene, to.x, to.y, 0x6ec1ff, 28, { add: true, width: 3 });
        spawnImpactRing(scene, to.x, to.y, 0xffffff, 16, { add: true, width: 2, duration: 240 });
        burst(scene, to.x, to.y, 0x6ec1ff, 10, 70, 3, { add: true });
        burst(scene, to.x, to.y, 0xffffff, 5, 50, 2, { add: true });
        sparkleStars(scene, to.x, to.y, 0xb3e5fc, 4, 22);
      },
    });
    return;
  }

  // ---- Archer: arrow shot with bowstring snap + flight trail ----
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const sx = from.x + Math.cos(angle) * 16;
  const sy = from.y + Math.sin(angle) * 16;

  // Bowstring snap: a tiny perpendicular flash at draw point.
  const perp = angle + Math.PI / 2;
  const snap = scene.add.rectangle(sx, sy, 14, 2, 0xffffff, 0.9).setDepth(20);
  snap.setRotation(perp);
  additive(snap);
  scene.tweens.add({
    targets: snap, scaleX: 0.2, alpha: 0, duration: 160,
    onComplete: () => snap.destroy(),
  });

  const proj = scene.add.sprite(sx, sy, FX.arrow).setDepth(21);
  proj.setRotation(angle);
  const glow = scene.add.sprite(sx, sy, FX.arrow).setDepth(20).setScale(1.4).setTint(0xfff59d).setAlpha(0.4);
  glow.setRotation(angle);
  additive(glow);
  const trail = emitTrail(scene, proj, 0xfff59d, 28, 200, 1.0, 0.4);

  const dist = Phaser.Math.Distance.Between(from.x, from.y, to.x, to.y);
  const dur = Math.min(420, Math.max(120, dist * 1.4));
  scene.tweens.add({
    targets: [proj, glow], x: to.x, y: to.y,
    duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove();
      proj.destroy();
      glow.destroy();
      onImpact?.();
      // Short hit-streak along arrow direction.
      const streak = scene.add.rectangle(to.x, to.y, 24, 3, 0xfff59d, 0.85).setDepth(22);
      streak.setRotation(angle);
      additive(streak);
      scene.tweens.add({
        targets: streak, scaleX: 0.2, alpha: 0, duration: 240,
        onComplete: () => streak.destroy(),
      });
      spawnImpactRing(scene, to.x, to.y, 0xffe082, 26, { add: true, width: 3 });
      spawnImpactRing(scene, to.x, to.y, 0xffffff, 14, { add: true, width: 2, duration: 220 });
      burst(scene, to.x, to.y, 0xffe082, 8, 70, 3, { add: true });
      burst(scene, to.x, to.y, 0xfff59d, 4, 50, 2, { add: true });
      sparkleStars(scene, to.x, to.y, 0xffffff, 3, 18);
    },
  });
}

// ----- Skill effects -----
export interface SkillFxOpts {
  caster?: { x: number; y: number };
  target: { x: number; y: number };
  onImpact?: () => void;
}

export function spawnSkillFx(scene: Phaser.Scene, skillId: number, opts: SkillFxOpts) {
  switch (skillId) {
    // ===== Warrior A: Precision =====
    case 1001: return castSlash(scene, opts);              // Power Slash
    case 1002: return castDeathStab(scene, opts);          // Death Stab
    case 1003: return castCrescentMoon(scene, opts);       // Crescent Moon Slash
    case 1004: return castStrikeOfDestruction(scene, opts);// Strike of Destruction
    // ===== Warrior B: Berserker =====
    case 1010: return castWhirlwind(scene, opts);          // Twisting Slash
    case 1011: return castRagefulBlow(scene, opts);        // Rageful Blow
    case 1012: return castBloodlust(scene, opts);          // Bloodlust (buff)
    case 1013: return castEarthquake(scene, opts);         // Earthquake
    // ===== Warrior C: Guardian =====
    case 1020: return castIronShield(scene, opts);         // Defense Stance
    case 1021: return castGreaterFortitude(scene, opts);   // Greater Fortitude
    case 1022: return castIronWill(scene, opts);           // Iron Will
    case 1023: return castLastStand(scene, opts);          // Last Stand
    // ===== Warrior M: Master =====
    case 1090: return castDragonsWrath(scene, opts);       // Dragon's Wrath
    case 1091: return castHeavensPillar(scene, opts);      // Heaven's Pillar
    // ===== Wizard A: Fire =====
    case 2001: return castFireball(scene, opts);           // Fire Ball
    case 2002: return castFireSlash(scene, opts);          // Fire Slash
    case 2003: return castMeteorStrike(scene, opts);       // Meteor Strike
    case 2004: return castInferno(scene, opts);            // Inferno
    // ===== Wizard B: Ice =====
    case 2010: return castIceBolt(scene, opts);            // Ice Bolt
    case 2011: return castIceStorm(scene, opts);           // Ice Storm
    case 2012: return castFrozenTomb(scene, opts);         // Frozen Tomb
    case 2013: return castGlacier(scene, opts);            // Glacier
    // ===== Wizard C: Arcane =====
    case 2020: return castManaShield(scene, opts);         // Mana Shield
    case 2021: return castTeleport(scene, opts);           // Teleport
    case 2022: return castEvilSpirit(scene, opts);         // Evil Spirit
    case 2023: return castSoulBarrier(scene, opts);        // Soul Barrier
    // ===== Wizard M: Master =====
    case 2090: return castNova(scene, opts);               // Nova
    case 2091: return castTimeStop(scene, opts);           // Time Stop
    // ===== Archer A: Precision =====
    case 3001: return castTripleShot(scene, opts);         // Triple Shot
    case 3002: return castPiercingArrow(scene, opts);      // Penetration
    case 3003: return castBoneSpirit(scene, opts);         // Bone Spirit
    case 3004: return castOneShotOneKill(scene, opts);     // One Shot One Kill
    // ===== Archer B: Volley =====
    case 3010: return castMultiShot(scene, opts);          // Multi-Shot
    case 3011: return castIceArrow(scene, opts);           // Ice Arrow
    case 3012: return castArrowRain(scene, opts);          // Rain of Arrows
    case 3013: return castStormOfArrows(scene, opts);      // Storm of Arrows
    // ===== Archer C: Support =====
    case 3020: return castHeal(scene, opts);               // Heal
    case 3021: return castGreaterDefense(scene, opts);     // Greater Defense
    case 3022: return castGreaterDamage(scene, opts);      // Greater Damage
    case 3023: return castSummonGoddess(scene, opts);      // Summon Goddess
    // ===== Archer M: Master =====
    case 3090: return castPhoenixArrow(scene, opts);       // Phoenix Arrow
    case 3091: return castHawkEye(scene, opts);            // Hawk Eye
    default: {
      opts.onImpact?.();
      spawnImpactRing(scene, opts.target.x, opts.target.y, 0xffffff, 30, { add: true });
    }
  }
}

// ===== Warrior =====

function castSlash(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const mx = from.x + Math.cos(angle) * 32;
  const my = from.y + Math.sin(angle) * 32;

  // Two crossing slashes for a "double cleave" feel.
  for (let i = 0; i < 2; i++) {
    const slash = scene.add.sprite(mx, my, FX.slash).setDepth(22);
    additive(slash);
    const baseRot = angle + (i === 0 ? -Math.PI / 4 : -Math.PI / 8);
    slash.setRotation(baseRot);
    slash.setScale(0.4, 0.5);
    scene.tweens.add({
      targets: slash,
      scale: 1.4,
      alpha: { from: 1, to: 0 },
      rotation: baseRot + Math.PI / 2,
      duration: 280,
      delay: i * 70,
      onComplete: () => slash.destroy(),
    });
  }

  scene.time.delayedCall(140, () => {
    onImpact?.();
    spawnShockwave(scene, target.x, target.y, 0xff1744, 36, 320);
    spawnImpactRing(scene, target.x, target.y, 0xff5252, 32, { add: true, width: 4 });
    spawnImpactRing(scene, target.x, target.y, 0xffab40, 22, { add: true, width: 2, duration: 260 });
    burst(scene, target.x, target.y, 0xff8a80, 12, 90, 3, { add: true });
    burst(scene, target.x, target.y, 0xffd180, 6, 60, 2, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 4, 24);
    shake(scene, 80, 0.003);
  });
}

function castWhirlwind(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;

  // Two counter-rotating whirlwind sprites for richer motion.
  const ww1 = scene.add.sprite(cx, cy, FX.whirlwind).setDepth(22).setAlpha(0.95);
  ww1.setScale(0.4); additive(ww1);
  const ww2 = scene.add.sprite(cx, cy, FX.whirlwind).setDepth(21).setAlpha(0.6);
  ww2.setScale(0.5); additive(ww2);

  scene.tweens.add({
    targets: ww1, scale: 1.8, rotation: Math.PI * 4,
    alpha: { from: 0.95, to: 0 }, duration: 700,
    onComplete: () => ww1.destroy(),
  });
  scene.tweens.add({
    targets: ww2, scale: 2.0, rotation: -Math.PI * 3,
    alpha: { from: 0.6, to: 0 }, duration: 700,
    onComplete: () => ww2.destroy(),
  });

  // Multi-wave shockwaves.
  for (let i = 0; i < 4; i++) {
    scene.time.delayedCall(i * 90, () => {
      spawnImpactRing(scene, cx, cy, 0xffab40, 80, { add: true, width: 4 });
      if (i % 2 === 0) spawnShockwave(scene, cx, cy, 0xffab40, 70, 360);
    });
  }

  // Orbiting flame motes.
  for (let i = 0; i < 10; i++) {
    const mote = scene.add.circle(cx, cy, 4, 0xffd180, 1).setDepth(23);
    additive(mote);
    const startA = (Math.PI * 2 * i) / 10;
    const radius = 70;
    scene.tweens.add({
      targets: { t: 0 }, t: 1, duration: 600 + i * 20,
      onUpdate: (tw) => {
        const p = tw.progress;
        const a = startA + p * Math.PI * 2;
        mote.x = cx + Math.cos(a) * radius * (1 - p * 0.4);
        mote.y = cy + Math.sin(a) * radius * (1 - p * 0.4);
        mote.setAlpha(1 - p);
      },
      onComplete: () => mote.destroy(),
    });
  }

  scene.time.delayedCall(180, () => {
    onImpact?.();
    burst(scene, target.x, target.y, 0xffab40, 18, 110, 4, { add: true });
    burst(scene, target.x, target.y, 0xfff3a0, 10, 80, 3, { add: true });
    shake(scene, 140, 0.005);
  });
}

function castIronShield(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  const sh = scene.add.sprite(cx, cy, FX.shield).setDepth(22).setAlpha(0);
  sh.setScale(0.5); additive(sh);
  scene.tweens.add({
    targets: sh, alpha: { from: 0, to: 0.95 }, scale: 1.3,
    duration: 220, yoyo: true, hold: 700,
    onComplete: () => sh.destroy(),
  });
  // Slow rotating outer ring.
  scene.tweens.add({ targets: sh, rotation: Math.PI, duration: 1200 });

  spawnImpactRing(scene, cx, cy, 0xb0bec5, 60, { add: true, width: 4 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 40, { add: true, width: 2, duration: 380 });
  sparkleStars(scene, cx, cy, 0xeceff1, 8, 50);
  // Rising hex motes.
  for (let i = 0; i < 8; i++) {
    const ox = Phaser.Math.Between(-30, 30);
    const m = scene.add.rectangle(cx + ox, cy + 18, 4, 4, 0xb0bec5, 1).setDepth(23).setAngle(45);
    additive(m);
    scene.tweens.add({
      targets: m, y: cy - 30 - Math.random() * 20, alpha: 0,
      delay: i * 30, duration: 600, ease: 'Cubic.easeOut',
      onComplete: () => m.destroy(),
    });
  }
  onImpact?.();
}

// ===== Wizard =====

function castFireball(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const ball = scene.add.sprite(from.x, from.y, FX.fireball).setDepth(22).setScale(0.9);
  additive(ball);
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  ball.setRotation(angle);

  // Pulsate.
  const pulseTw = scene.tweens.add({
    targets: ball, scale: { from: 0.9, to: 1.25 }, duration: 180, yoyo: true, repeat: -1,
  });

  // Bright trail using afterimages.
  const trail = emitTrail(scene, ball, 0xff8c1a, 22, 320, 1.0, 0.55);
  // Small ember puffs.
  const embers = scene.time.addEvent({
    delay: 30, loop: true, callback: () => {
      const t = scene.add.circle(
        ball.x + Phaser.Math.Between(-3, 3),
        ball.y + Phaser.Math.Between(-3, 3),
        Phaser.Math.Between(3, 6), 0xffd166, 0.85,
      ).setDepth(21);
      additive(t);
      scene.tweens.add({
        targets: t, alpha: 0, scale: 0.3, duration: 320,
        onComplete: () => t.destroy(),
      });
    },
  });

  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(540, Math.max(160, dist * 1.6));
  scene.tweens.add({
    targets: ball, x: target.x, y: target.y,
    duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove();
      embers.remove();
      pulseTw.remove();
      ball.destroy();
      onImpact?.();
      // Big explosion.
      flash(scene, 0xff8c1a, 80, 0.18);
      spawnShockwave(scene, target.x, target.y, 0xff5722, 70, 420);
      spawnImpactRing(scene, target.x, target.y, 0xff5722, 60, { add: true, width: 5 });
      spawnImpactRing(scene, target.x, target.y, 0xffc107, 38, { add: true, width: 3, duration: 340 });
      spawnImpactRing(scene, target.x, target.y, 0xffffff, 22, { add: true, width: 2, duration: 280 });
      burst(scene, target.x, target.y, 0xff8c1a, 22, 130, 4, { add: true });
      burst(scene, target.x, target.y, 0xffd166, 12, 90, 3, { add: true });
      sparkleStars(scene, target.x, target.y, 0xffffff, 6, 30);
      shake(scene, 160, 0.006);
    },
  });
}

function castManaShield(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  const sh = scene.add.sprite(cx, cy, FX.manaShield).setDepth(22).setAlpha(0);
  sh.setScale(0.5); additive(sh);
  scene.tweens.add({
    targets: sh, alpha: { from: 0, to: 0.9 }, scale: 1.4,
    duration: 260, yoyo: true, hold: 750,
    onComplete: () => sh.destroy(),
  });
  scene.tweens.add({ targets: sh, rotation: -Math.PI, duration: 1400 });

  spawnImpactRing(scene, cx, cy, 0x42a5f5, 60, { add: true, width: 5 });
  spawnImpactRing(scene, cx, cy, 0x90caf9, 78, { add: true, width: 3, duration: 500 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 36, { add: true, width: 2, duration: 380 });
  sparkleStars(scene, cx, cy, 0x90caf9, 10, 56);

  // Orbiting blue runes.
  for (let i = 0; i < 6; i++) {
    const a0 = (Math.PI * 2 * i) / 6;
    const rune = scene.add.rectangle(cx, cy, 6, 6, 0x90caf9, 1).setDepth(23).setAngle(45);
    additive(rune);
    scene.tweens.add({
      targets: { t: 0 }, t: 1, duration: 900,
      onUpdate: (tw) => {
        const p = tw.progress;
        const a = a0 + p * Math.PI * 2;
        const r = 50 * (1 - p * 0.3);
        rune.x = cx + Math.cos(a) * r;
        rune.y = cy + Math.sin(a) * r;
        rune.setAlpha(1 - p);
      },
      onComplete: () => rune.destroy(),
    });
  }
  onImpact?.();
}

// ===== Archer =====

function castPiercingArrow(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const ex = target.x + Math.cos(angle) * 50;
  const ey = target.y + Math.sin(angle) * 50;

  const arrow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(22).setScale(1.3);
  arrow.setRotation(angle);
  const glow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(21).setScale(1.8).setTint(0xfff59d).setAlpha(0.55);
  glow.setRotation(angle);
  additive(glow);
  const trail = emitTrail(scene, arrow, 0xfff59d, 22, 260, 1.2, 0.5);

  const dist = Phaser.Math.Distance.Between(from.x, from.y, ex, ey);
  const dur = Math.min(360, Math.max(140, dist * 1.0));
  scene.tweens.add({
    targets: [arrow, glow], x: ex, y: ey, duration: dur, ease: 'Linear',
    onComplete: () => { trail.remove(); arrow.destroy(); glow.destroy(); },
  });

  scene.time.delayedCall(dur * 0.55, () => {
    onImpact?.();
    // Pierce streak: a short horizontal streak along the angle.
    const streak = scene.add.rectangle(target.x, target.y, 50, 4, 0xfff59d, 0.85).setDepth(22);
    streak.setRotation(angle);
    additive(streak);
    scene.tweens.add({
      targets: streak, scaleX: 0.2, alpha: 0, duration: 280,
      onComplete: () => streak.destroy(),
    });
    spawnShockwave(scene, target.x, target.y, 0xfff176, 40, 300);
    spawnImpactRing(scene, target.x, target.y, 0xfff176, 36, { add: true, width: 3 });
    spawnImpactRing(scene, target.x, target.y, 0xffffff, 20, { add: true, width: 2, duration: 260 });
    burst(scene, target.x, target.y, 0xfff59d, 14, 100, 3, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 5, 26);
    shake(scene, 60, 0.003);
  });
}

function castArrowRain(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Telegraph circle.
  const tele = scene.add.graphics({ x: target.x, y: target.y }).setDepth(20);
  additive(tele);
  tele.lineStyle(2, 0xa1887f, 0.7).strokeCircle(0, 0, 70);
  tele.fillStyle(0xa1887f, 0.12).fillCircle(0, 0, 70);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 700, onComplete: () => tele.destroy() });

  const N = 14;
  for (let i = 0; i < N; i++) {
    const ox = Phaser.Math.Between(-65, 65);
    const oy = Phaser.Math.Between(-45, 45);
    const tx = target.x + ox;
    const ty = target.y + oy;
    const sx = tx - 60;
    const sy = ty - 240;
    const arr = scene.add.sprite(sx, sy, FX.arrow).setDepth(22);
    arr.setRotation(Math.atan2(ty - sy, tx - sx));
    const trail = emitTrail(scene, arr, 0xfff59d, 28, 180, 0.9, 0.35);
    scene.tweens.add({
      targets: arr, x: tx, y: ty,
      delay: i * 35, duration: 320, ease: 'Quad.easeIn',
      onComplete: () => {
        trail.remove();
        arr.destroy();
        spawnImpactRing(scene, tx, ty, 0xa1887f, 22, { add: true, width: 2 });
        spawnShockwave(scene, tx, ty, 0xd7ccc8, 16, 240);
        burst(scene, tx, ty, 0xd7ccc8, 6, 60, 2, { add: true });
      },
    });
  }
  scene.time.delayedCall(220, () => { onImpact?.(); shake(scene, 80, 0.003); });
  scene.time.delayedCall(N * 35 + 200, () => shake(scene, 100, 0.004));
}

function castHeal(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = (caster ?? target).x;
  const cy = (caster ?? target).y;
  const heal = scene.add.sprite(cx, cy, FX.heal).setDepth(22).setAlpha(0).setScale(0.5);
  additive(heal);
  scene.tweens.add({
    targets: heal, alpha: { from: 0, to: 0.95 }, scale: 1.5, y: cy - 16,
    duration: 550, yoyo: true, onComplete: () => heal.destroy(),
  });
  scene.tweens.add({ targets: heal, rotation: Math.PI / 6, duration: 1100, yoyo: true });

  // Radiant aura ring.
  spawnImpactRing(scene, cx, cy, 0x66bb6a, 48, { add: true, width: 4 });
  spawnImpactRing(scene, cx, cy, 0xc8e6c9, 30, { add: true, width: 2, duration: 360 });
  spawnShockwave(scene, cx, cy, 0x81c784, 40, 500);

  // Rising sparkles, varied size & color.
  for (let i = 0; i < 18; i++) {
    const ox = Phaser.Math.Between(-26, 26);
    const startY = cy + 14;
    const isBig = Math.random() < 0.3;
    const r = Math.random() < 0.5 ? 0x66bb6a : 0xc8e6c9;
    const sp = scene.add.circle(cx + ox, startY, isBig ? 4 : 2.5, r, 1).setDepth(23);
    additive(sp);
    scene.tweens.add({
      targets: sp, y: cy - 50 - Math.random() * 26, alpha: 0,
      delay: i * 22, duration: 700, ease: 'Cubic.easeOut',
      onComplete: () => sp.destroy(),
    });
  }
  // Floating leaf-like rectangles.
  for (let i = 0; i < 6; i++) {
    const ox = Phaser.Math.Between(-26, 26);
    const leaf = scene.add.rectangle(cx + ox, cy + 10, 6, 3, 0x81c784, 1).setDepth(24);
    additive(leaf);
    scene.tweens.add({
      targets: leaf, y: cy - 40 - Math.random() * 20,
      x: leaf.x + Phaser.Math.Between(-12, 12),
      angle: Phaser.Math.Between(-180, 180),
      alpha: 0, delay: i * 40, duration: 850, ease: 'Sine.easeOut',
      onComplete: () => leaf.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xc8e6c9, 6, 36);
  flash(scene, 0x66bb6a, 80, 0.08);
  onImpact?.();
}

// --- Warrior A: Death Stab (1002) ---
function castDeathStab(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const mx = from.x + Math.cos(angle) * 36;
  const my = from.y + Math.sin(angle) * 36;

  // Thrust line.
  const thrust = scene.add.rectangle(mx, my, 60, 5, 0xff5252, 1).setDepth(22).setRotation(angle);
  additive(thrust);
  scene.tweens.add({
    targets: thrust, scaleX: 1.8, alpha: 0, duration: 240, ease: 'Cubic.easeOut',
    onComplete: () => thrust.destroy(),
  });
  // Armor-pierce sparks.
  const ghost = scene.add.rectangle(mx, my, 50, 3, 0xffffff, 0.7).setDepth(23).setRotation(angle);
  additive(ghost);
  scene.tweens.add({
    targets: ghost, scaleX: 2.0, alpha: 0, duration: 300, delay: 40,
    onComplete: () => ghost.destroy(),
  });

  scene.time.delayedCall(140, () => {
    onImpact?.();
    spawnShockwave(scene, target.x, target.y, 0xff1744, 32, 300);
    spawnImpactRing(scene, target.x, target.y, 0xff5252, 28, { add: true, width: 4 });
    burst(scene, target.x, target.y, 0xff8a80, 10, 80, 3, { add: true });
    // Armor crack lines.
    for (let i = 0; i < 4; i++) {
      const a = angle + (i - 1.5) * 0.5;
      const line = scene.add.rectangle(target.x, target.y, 20, 2, 0xffffff, 0.9).setDepth(24).setRotation(a);
      additive(line);
      scene.tweens.add({
        targets: line, scaleX: 2.5, alpha: 0, duration: 320, delay: i * 30,
        onComplete: () => line.destroy(),
      });
    }
    shake(scene, 100, 0.004);
  });
}

// --- Warrior A: Crescent Moon Slash (1003) ---
function castCrescentMoon(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const mx = from.x + Math.cos(angle) * 30;
  const my = from.y + Math.sin(angle) * 30;

  // 3 sequential crescent slashes.
  for (let i = 0; i < 3; i++) {
    const slash = scene.add.sprite(mx, my, FX.slash).setDepth(22);
    additive(slash);
    const offset = (i - 1) * 0.35;
    const baseRot = angle + offset - Math.PI / 4;
    slash.setRotation(baseRot).setScale(0.3 + i * 0.15);
    slash.setTint(i === 2 ? 0xffffff : 0xb3e5fc);
    scene.tweens.add({
      targets: slash, scale: 1.3 + i * 0.2, alpha: { from: 1, to: 0 },
      rotation: baseRot + Math.PI / 2, duration: 280, delay: i * 100,
      onComplete: () => slash.destroy(),
    });
  }

  scene.time.delayedCall(300, () => {
    onImpact?.();
    flash(scene, 0xb3e5fc, 60, 0.12);
    spawnShockwave(scene, target.x, target.y, 0x90caf9, 44, 360);
    spawnImpactRing(scene, target.x, target.y, 0xb3e5fc, 36, { add: true, width: 4 });
    burst(scene, target.x, target.y, 0xb3e5fc, 16, 100, 3, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 6, 28);
    shake(scene, 120, 0.005);
  });
}

// --- Warrior A: Strike of Destruction (1004) ---
function castStrikeOfDestruction(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;

  // Weapon raised overhead flash.
  const chargeFlash = scene.add.circle(cx, cy - 20, 8, 0xffffff, 1).setDepth(24);
  additive(chargeFlash);
  scene.tweens.add({
    targets: chargeFlash, scale: { from: 0.5, to: 3 }, alpha: 0, duration: 300,
    onComplete: () => chargeFlash.destroy(),
  });

  scene.time.delayedCall(200, () => {
    // Ground slam.
    flash(scene, 0xff5722, 100, 0.2);
    spawnShockwave(scene, target.x, target.y, 0xff5722, 100, 500);
    spawnShockwave(scene, target.x, target.y, 0xffc107, 70, 400);
    spawnImpactRing(scene, target.x, target.y, 0xff5722, 80, { add: true, width: 6 });
    spawnImpactRing(scene, target.x, target.y, 0xffffff, 50, { add: true, width: 3, duration: 400 });
    // Ground crack lines radiating out.
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI * 2 * i) / 8;
      const crack = scene.add.rectangle(target.x, target.y, 60, 3, 0xff8a80, 1).setDepth(22).setRotation(a);
      additive(crack);
      scene.tweens.add({
        targets: crack, scaleX: 2.5, alpha: 0, duration: 500, delay: i * 20,
        onComplete: () => crack.destroy(),
      });
    }
    burst(scene, target.x, target.y, 0xff5722, 24, 140, 5, { add: true });
    burst(scene, target.x, target.y, 0xffd180, 14, 100, 3, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 8, 50);
    shake(scene, 250, 0.01);
    onImpact?.();
  });
}

// --- Warrior B: Rageful Blow (1011) ---
function castRagefulBlow(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;

  // Red rage aura on caster.
  spawnShockwave(scene, cx, cy, 0xff1744, 50, 300);

  scene.time.delayedCall(120, () => {
    // Overhead slam.
    const slam = scene.add.sprite(target.x, target.y, FX.slash).setDepth(22).setScale(0.5);
    additive(slam);
    slam.setTint(0xff1744);
    scene.tweens.add({
      targets: slam, scale: 2.0, alpha: 0, rotation: Math.PI, duration: 400,
      onComplete: () => slam.destroy(),
    });
    spawnShockwave(scene, target.x, target.y, 0xff1744, 80, 450);
    spawnImpactRing(scene, target.x, target.y, 0xff5252, 60, { add: true, width: 5 });
    // Stun stars.
    for (let i = 0; i < 5; i++) {
      const a = (Math.PI * 2 * i) / 5;
      const star = scene.add.rectangle(
        target.x + Math.cos(a) * 20, target.y - 20 + Math.sin(a) * 10,
        6, 6, 0xffd166, 1,
      ).setDepth(25).setAngle(45);
      additive(star);
      scene.tweens.add({
        targets: star, y: star.y - 10, alpha: 0, angle: 225,
        duration: 600, delay: 200 + i * 60, ease: 'Cubic.easeOut',
        onComplete: () => star.destroy(),
      });
    }
    burst(scene, target.x, target.y, 0xff1744, 18, 120, 4, { add: true });
    shake(scene, 180, 0.007);
    onImpact?.();
  });
}

// --- Warrior B: Bloodlust (1012) ---
function castBloodlust(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;

  // Red pulsing aura.
  for (let i = 0; i < 3; i++) {
    scene.time.delayedCall(i * 120, () => {
      spawnImpactRing(scene, cx, cy, 0xff1744, 50 + i * 10, { add: true, width: 3 });
    });
  }
  // Rising blood motes.
  for (let i = 0; i < 12; i++) {
    const ox = Phaser.Math.Between(-24, 24);
    const m = scene.add.circle(cx + ox, cy + 16, 3, 0xff1744, 1).setDepth(23);
    additive(m);
    scene.tweens.add({
      targets: m, y: cy - 40 - Math.random() * 20, alpha: 0,
      delay: i * 25, duration: 700, ease: 'Cubic.easeOut',
      onComplete: () => m.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xff5252, 6, 40);
  flash(scene, 0xff1744, 100, 0.1);
  onImpact?.();
}

// --- Warrior B: Earthquake (1013) ---
function castEarthquake(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;

  // Stomp flash.
  const stomp = scene.add.circle(cx, cy + 10, 10, 0x8d6e63, 1).setDepth(20);
  scene.tweens.add({
    targets: stomp, scale: 3, alpha: 0, duration: 300,
    onComplete: () => stomp.destroy(),
  });

  scene.time.delayedCall(150, () => {
    flash(scene, 0x8d6e63, 120, 0.15);
    // Multiple expanding earth rings.
    for (let i = 0; i < 4; i++) {
      scene.time.delayedCall(i * 80, () => {
        spawnShockwave(scene, target.x, target.y, 0x8d6e63, 100 + i * 20, 500);
        spawnImpactRing(scene, target.x, target.y, 0xa1887f, 90 + i * 15, { add: true, width: 4 });
      });
    }
    // Rock debris flying up.
    for (let i = 0; i < 16; i++) {
      const ox = Phaser.Math.Between(-80, 80);
      const oy = Phaser.Math.Between(-50, 50);
      const rock = scene.add.rectangle(target.x + ox, target.y + oy, 6, 6, 0x6d4c41, 1).setDepth(23);
      scene.tweens.add({
        targets: rock, y: rock.y - 40 - Math.random() * 30, alpha: 0,
        angle: Phaser.Math.Between(-180, 180),
        delay: i * 20, duration: 600, ease: 'Cubic.easeOut',
        onComplete: () => rock.destroy(),
      });
    }
    burst(scene, target.x, target.y, 0x8d6e63, 20, 130, 5, { add: true });
    shake(scene, 350, 0.012);
    onImpact?.();
  });
}

// --- Warrior C: Greater Fortitude (1021) ---
function castGreaterFortitude(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  spawnImpactRing(scene, cx, cy, 0x66bb6a, 60, { add: true, width: 4 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 40, { add: true, width: 2, duration: 400 });
  spawnShockwave(scene, cx, cy, 0x66bb6a, 50, 400);
  // Rising green shields.
  for (let i = 0; i < 8; i++) {
    const ox = Phaser.Math.Between(-28, 28);
    const m = scene.add.rectangle(cx + ox, cy + 14, 5, 5, 0x66bb6a, 1).setDepth(23).setAngle(45);
    additive(m);
    scene.tweens.add({
      targets: m, y: cy - 36 - Math.random() * 20, alpha: 0,
      delay: i * 28, duration: 650, ease: 'Cubic.easeOut',
      onComplete: () => m.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xc8e6c9, 6, 44);
  onImpact?.();
}

// --- Warrior C: Iron Will (1022) ---
function castIronWill(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  // Golden shield burst.
  const sh = scene.add.sprite(cx, cy, FX.shield).setDepth(22).setAlpha(0).setScale(0.4);
  sh.setTint(0xffd166);
  additive(sh);
  scene.tweens.add({
    targets: sh, alpha: { from: 0, to: 1 }, scale: 1.5,
    duration: 250, yoyo: true, hold: 500,
    onComplete: () => sh.destroy(),
  });
  spawnImpactRing(scene, cx, cy, 0xffd166, 55, { add: true, width: 5 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 35, { add: true, width: 3, duration: 400 });
  flash(scene, 0xffd166, 80, 0.12);
  sparkleStars(scene, cx, cy, 0xffd166, 8, 48);
  onImpact?.();
}

// --- Warrior C: Last Stand (1023) ---
function castLastStand(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  // Golden invincibility burst.
  flash(scene, 0xffd166, 150, 0.2);
  spawnShockwave(scene, cx, cy, 0xffd166, 80, 500);
  spawnImpactRing(scene, cx, cy, 0xffd166, 70, { add: true, width: 6 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 50, { add: true, width: 3, duration: 450 });
  // Radiant pillars.
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI * 2 * i) / 6;
    const px = cx + Math.cos(a) * 40;
    const py = cy + Math.sin(a) * 40;
    const pillar = scene.add.rectangle(px, py, 4, 30, 0xffd166, 1).setDepth(24);
    additive(pillar);
    scene.tweens.add({
      targets: pillar, y: py - 50, scaleY: 2, alpha: 0,
      duration: 600, delay: i * 40, ease: 'Cubic.easeOut',
      onComplete: () => pillar.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xffffff, 10, 50);
  onImpact?.();
}

// --- Warrior M: Dragon's Wrath (1090) ---
function castDragonsWrath(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;

  // Fire charge at caster.
  spawnShockwave(scene, cx, cy, 0xff5722, 60, 300);

  scene.time.delayedCall(200, () => {
    // Massive fire explosion at target.
    flash(scene, 0xff5722, 140, 0.25);
    for (let i = 0; i < 3; i++) {
      scene.time.delayedCall(i * 80, () => {
        spawnShockwave(scene, target.x, target.y, 0xff5722, 120 + i * 20, 500);
        spawnImpactRing(scene, target.x, target.y, 0xff8c1a, 100 + i * 15, { add: true, width: 5 });
      });
    }
    // Dragon fire breath particles.
    for (let i = 0; i < 24; i++) {
      const ox = Phaser.Math.Between(-90, 90);
      const oy = Phaser.Math.Between(-60, 60);
      const flame = scene.add.circle(target.x + ox, target.y + oy, Phaser.Math.Between(4, 8), 0xff8c1a, 1).setDepth(23);
      additive(flame);
      scene.tweens.add({
        targets: flame, y: flame.y - 40 - Math.random() * 30, alpha: 0, scale: 0.3,
        delay: i * 15, duration: 600, ease: 'Cubic.easeOut',
        onComplete: () => flame.destroy(),
      });
    }
    burst(scene, target.x, target.y, 0xff5722, 28, 160, 5, { add: true });
    burst(scene, target.x, target.y, 0xffd166, 16, 120, 4, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 10, 60);
    shake(scene, 300, 0.015);
    onImpact?.();
  });
}

// --- Warrior M: Heaven's Pillar (1091) ---
function castHeavensPillar(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Light beam from sky.
  const beam = scene.add.rectangle(target.x, target.y - 200, 20, 400, 0xffd166, 0).setDepth(24);
  additive(beam);
  scene.tweens.add({
    targets: beam, alpha: { from: 0, to: 0.9 }, scaleX: 3, duration: 300,
    yoyo: true, hold: 400,
    onComplete: () => beam.destroy(),
  });
  scene.time.delayedCall(250, () => {
    flash(scene, 0xffd166, 120, 0.25);
    spawnShockwave(scene, target.x, target.y, 0xffd166, 110, 500);
    spawnShockwave(scene, target.x, target.y, 0xffffff, 80, 400);
    spawnImpactRing(scene, target.x, target.y, 0xffd166, 100, { add: true, width: 6 });
    // Stun stars.
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      const star = scene.add.rectangle(
        target.x + Math.cos(a) * 30, target.y - 16 + Math.sin(a) * 14,
        7, 7, 0xffd166, 1,
      ).setDepth(25).setAngle(45);
      additive(star);
      scene.tweens.add({
        targets: star, y: star.y - 14, alpha: 0, angle: 225,
        duration: 700, delay: 200 + i * 50, ease: 'Cubic.easeOut',
        onComplete: () => star.destroy(),
      });
    }
    burst(scene, target.x, target.y, 0xffd166, 24, 140, 5, { add: true });
    sparkleStars(scene, target.x, target.y, 0xffffff, 10, 60);
    shake(scene, 250, 0.012);
    onImpact?.();
  });
}

// --- Wizard A: Fire Slash (2002) ---
function castFireSlash(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  // Fire blade projectile.
  const blade = scene.add.sprite(from.x, from.y, FX.slash).setDepth(22).setScale(0.5).setTint(0xff5722);
  additive(blade);
  blade.setRotation(angle);
  const trail = emitTrail(scene, blade, 0xff8c1a, 20, 280, 1.0, 0.5);
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(400, Math.max(140, dist * 1.2));
  scene.tweens.add({
    targets: blade, x: target.x, y: target.y, scale: 1.4, duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove(); blade.destroy();
      onImpact?.();
      flash(scene, 0xff5722, 70, 0.14);
      spawnShockwave(scene, target.x, target.y, 0xff5722, 50, 380);
      spawnImpactRing(scene, target.x, target.y, 0xff8c1a, 40, { add: true, width: 4 });
      burst(scene, target.x, target.y, 0xff8c1a, 16, 110, 4, { add: true });
      sparkleStars(scene, target.x, target.y, 0xffd166, 5, 28);
      shake(scene, 100, 0.005);
    },
  });
}

// --- Wizard A: Meteor Strike (2003) ---
function castMeteorStrike(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Telegraph.
  const tele = scene.add.graphics({ x: target.x, y: target.y }).setDepth(20);
  tele.lineStyle(2, 0xff5722, 0.6).strokeCircle(0, 0, 60);
  tele.fillStyle(0xff5722, 0.1).fillCircle(0, 0, 60);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 800, onComplete: () => tele.destroy() });

  // Meteor falling from top-right.
  const meteor = scene.add.sprite(target.x + 120, target.y - 250, FX.fireball).setDepth(24).setScale(2);
  additive(meteor);
  const mTrail = emitTrail(scene, meteor, 0xff5722, 18, 400, 1.5, 0.6);
  scene.tweens.add({
    targets: meteor, x: target.x, y: target.y, scale: 1.2, duration: 500, ease: 'Quad.easeIn',
    onComplete: () => {
      mTrail.remove(); meteor.destroy();
      onImpact?.();
      flash(scene, 0xff5722, 120, 0.25);
      spawnShockwave(scene, target.x, target.y, 0xff5722, 90, 500);
      spawnShockwave(scene, target.x, target.y, 0xffc107, 60, 400);
      spawnImpactRing(scene, target.x, target.y, 0xff5722, 80, { add: true, width: 6 });
      spawnImpactRing(scene, target.x, target.y, 0xffffff, 50, { add: true, width: 3, duration: 400 });
      burst(scene, target.x, target.y, 0xff5722, 26, 150, 5, { add: true });
      burst(scene, target.x, target.y, 0xffd166, 14, 110, 4, { add: true });
      sparkleStars(scene, target.x, target.y, 0xffffff, 8, 50);
      shake(scene, 280, 0.012);
    },
  });
}

// --- Wizard A: Inferno (2004) ---
function castInferno(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Fire storm AoE — multiple fire columns rising.
  const tele = scene.add.graphics({ x: target.x, y: target.y }).setDepth(20);
  tele.lineStyle(2, 0xff5722, 0.5).strokeCircle(0, 0, 100);
  tele.fillStyle(0xff5722, 0.08).fillCircle(0, 0, 100);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 1200, onComplete: () => tele.destroy() });

  for (let i = 0; i < 8; i++) {
    const ox = Phaser.Math.Between(-80, 80);
    const oy = Phaser.Math.Between(-60, 60);
    const fx = target.x + ox;
    const fy = target.y + oy;
    scene.time.delayedCall(i * 80, () => {
      const col = scene.add.rectangle(fx, fy, 12, 40, 0xff5722, 0.9).setDepth(23);
      additive(col);
      scene.tweens.add({
        targets: col, scaleY: 3, y: fy - 30, alpha: 0, duration: 500,
        onComplete: () => col.destroy(),
      });
      spawnShockwave(scene, fx, fy, 0xff8c1a, 30, 300);
      burst(scene, fx, fy, 0xffd166, 6, 60, 3, { add: true });
    });
  }
  scene.time.delayedCall(300, () => {
    flash(scene, 0xff5722, 100, 0.2);
    onImpact?.();
    shake(scene, 200, 0.008);
  });
}

// --- Wizard B: Ice Bolt (2010) ---
function castIceBolt(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const proj = scene.add.sprite(from.x, from.y, FX.spark).setDepth(22).setScale(1.2).setTint(0x81d4fa);
  additive(proj);
  proj.setRotation(angle);
  const trail = emitTrail(scene, proj, 0x81d4fa, 22, 260, 1.1, 0.5);
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(440, Math.max(140, dist * 1.5));
  scene.tweens.add({
    targets: proj, x: target.x, y: target.y, duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove(); proj.destroy();
      onImpact?.();
      spawnShockwave(scene, target.x, target.y, 0x81d4fa, 36, 320);
      spawnImpactRing(scene, target.x, target.y, 0x81d4fa, 30, { add: true, width: 3 });
      burst(scene, target.x, target.y, 0xb3e5fc, 10, 80, 3, { add: true });
      // Ice crystal shards.
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI * 2 * i) / 4 + Math.random() * 0.5;
        const shard = scene.add.rectangle(target.x, target.y, 4, 10, 0xb3e5fc, 1).setDepth(24).setRotation(a);
        additive(shard);
        scene.tweens.add({
          targets: shard,
          x: target.x + Math.cos(a) * 30, y: target.y + Math.sin(a) * 30,
          alpha: 0, duration: 400, ease: 'Cubic.easeOut',
          onComplete: () => shard.destroy(),
        });
      }
      sparkleStars(scene, target.x, target.y, 0xe1f5fe, 4, 22);
    },
  });
}

// --- Wizard B: Ice Storm (2011) ---
function castIceStorm(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  const tele = scene.add.graphics({ x: target.x, y: target.y }).setDepth(20);
  tele.lineStyle(2, 0x81d4fa, 0.6).strokeCircle(0, 0, 70);
  tele.fillStyle(0x81d4fa, 0.1).fillCircle(0, 0, 70);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 800, onComplete: () => tele.destroy() });

  // Ice shards raining.
  for (let i = 0; i < 12; i++) {
    const ox = Phaser.Math.Between(-60, 60);
    const oy = Phaser.Math.Between(-40, 40);
    const tx = target.x + ox;
    const ty = target.y + oy;
    scene.time.delayedCall(i * 50, () => {
      const shard = scene.add.rectangle(tx, ty - 120, 5, 14, 0xb3e5fc, 1).setDepth(23);
      additive(shard);
      scene.tweens.add({
        targets: shard, y: ty, duration: 280, ease: 'Quad.easeIn',
        onComplete: () => {
          shard.destroy();
          spawnImpactRing(scene, tx, ty, 0x81d4fa, 20, { add: true, width: 2 });
          burst(scene, tx, ty, 0xb3e5fc, 4, 50, 2, { add: true });
        },
      });
    });
  }
  scene.time.delayedCall(300, () => {
    flash(scene, 0x81d4fa, 80, 0.12);
    onImpact?.();
    shake(scene, 120, 0.005);
  });
}

// --- Wizard B: Frozen Tomb (2012) ---
function castFrozenTomb(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Ice block encasing target.
  const block = scene.add.rectangle(target.x, target.y, 40, 50, 0x81d4fa, 0).setDepth(24);
  additive(block);
  scene.tweens.add({
    targets: block, alpha: { from: 0, to: 0.7 }, scaleX: 1.3, scaleY: 1.2,
    duration: 200, yoyo: true, hold: 2000,
    onComplete: () => {
      // Shatter.
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * 2 * i) / 8;
        const shard = scene.add.rectangle(target.x, target.y, 8, 12, 0xb3e5fc, 1).setDepth(25).setRotation(a);
        additive(shard);
        scene.tweens.add({
          targets: shard,
          x: target.x + Math.cos(a) * 50, y: target.y + Math.sin(a) * 50,
          alpha: 0, angle: Phaser.Math.Between(-180, 180), duration: 500,
          onComplete: () => shard.destroy(),
        });
      }
      spawnImpactRing(scene, target.x, target.y, 0x81d4fa, 50, { add: true, width: 4 });
      burst(scene, target.x, target.y, 0xb3e5fc, 12, 90, 3, { add: true });
      block.destroy();
    },
  });
  spawnImpactRing(scene, target.x, target.y, 0xb3e5fc, 36, { add: true, width: 3 });
  sparkleStars(scene, target.x, target.y, 0xe1f5fe, 6, 30);
  flash(scene, 0x81d4fa, 60, 0.1);
  onImpact?.();
}

// --- Wizard B: Glacier (2013) ---
function castGlacier(scene: Phaser.Scene, { target, onImpact }: SkillFxOpts) {
  // Giant ice pillar erupting from ground.
  const tele = scene.add.graphics({ x: target.x, y: target.y }).setDepth(20);
  tele.lineStyle(2, 0x81d4fa, 0.6).strokeCircle(0, 0, 80);
  tele.fillStyle(0x81d4fa, 0.1).fillCircle(0, 0, 80);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 600, onComplete: () => tele.destroy() });

  scene.time.delayedCall(200, () => {
    // Central ice pillar.
    const pillar = scene.add.rectangle(target.x, target.y + 20, 30, 80, 0x81d4fa, 0).setDepth(24);
    additive(pillar);
    scene.tweens.add({
      targets: pillar, alpha: { from: 0, to: 0.85 }, y: target.y - 20, scaleY: 1.5,
      duration: 300, yoyo: true, hold: 600,
      onComplete: () => pillar.destroy(),
    });
    // Surrounding ice spikes.
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      const sx = target.x + Math.cos(a) * 50;
      const sy = target.y + Math.sin(a) * 50;
      const spike = scene.add.rectangle(sx, sy + 10, 10, 30, 0xb3e5fc, 0).setDepth(23);
      additive(spike);
      scene.tweens.add({
        targets: spike, alpha: { from: 0, to: 0.8 }, y: sy - 10, scaleY: 1.3,
        duration: 250, delay: i * 40, yoyo: true, hold: 500,
        onComplete: () => spike.destroy(),
      });
    }
    flash(scene, 0x81d4fa, 100, 0.18);
    spawnShockwave(scene, target.x, target.y, 0x81d4fa, 90, 500);
    spawnImpactRing(scene, target.x, target.y, 0xb3e5fc, 80, { add: true, width: 5 });
    burst(scene, target.x, target.y, 0xb3e5fc, 20, 120, 4, { add: true });
    sparkleStars(scene, target.x, target.y, 0xe1f5fe, 8, 50);
    shake(scene, 200, 0.008);
    onImpact?.();
  });
}

// --- Wizard C: Teleport (2021) ---
function castTeleport(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  // Vanish at origin.
  spawnImpactRing(scene, from.x, from.y, 0x7e57c2, 40, { add: true, width: 3 });
  burst(scene, from.x, from.y, 0xb39ddb, 10, 80, 3, { add: true });
  // Appear at target.
  scene.time.delayedCall(150, () => {
    flash(scene, 0x7e57c2, 60, 0.1);
    spawnShockwave(scene, target.x, target.y, 0x7e57c2, 40, 300);
    spawnImpactRing(scene, target.x, target.y, 0xb39ddb, 40, { add: true, width: 3 });
    burst(scene, target.x, target.y, 0xd1c4e9, 10, 80, 3, { add: true });
    sparkleStars(scene, target.x, target.y, 0xe1bee7, 6, 30);
    onImpact?.();
  });
}

// --- Wizard C: Evil Spirit (2022) ---
function castEvilSpirit(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;
  // 4 dark spirits orbiting out.
  for (let i = 0; i < 4; i++) {
    const a0 = (Math.PI * 2 * i) / 4;
    const spirit = scene.add.circle(cx, cy, 6, 0x7e57c2, 1).setDepth(23);
    additive(spirit);
    scene.tweens.add({
      targets: { t: 0 }, t: 1, duration: 800,
      onUpdate: (tw) => {
        const p = tw.progress;
        const a = a0 + p * Math.PI * 3;
        const r = 20 + p * 60;
        spirit.x = cx + Math.cos(a) * r;
        spirit.y = cy + Math.sin(a) * r;
        spirit.setAlpha(1 - p * 0.7);
      },
      onComplete: () => spirit.destroy(),
    });
  }
  spawnImpactRing(scene, cx, cy, 0x7e57c2, 50, { add: true, width: 3 });
  spawnShockwave(scene, cx, cy, 0x4a148c, 40, 400);
  burst(scene, cx, cy, 0xb39ddb, 12, 80, 3, { add: true });
  flash(scene, 0x7e57c2, 80, 0.1);
  onImpact?.();
}

// --- Wizard C: Soul Barrier (2023) ---
function castSoulBarrier(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  const sh = scene.add.sprite(cx, cy, FX.manaShield).setDepth(22).setAlpha(0).setScale(0.4);
  sh.setTint(0x7e57c2);
  additive(sh);
  scene.tweens.add({
    targets: sh, alpha: { from: 0, to: 0.95 }, scale: 1.6,
    duration: 280, yoyo: true, hold: 800,
    onComplete: () => sh.destroy(),
  });
  scene.tweens.add({ targets: sh, rotation: -Math.PI * 2, duration: 1600 });
  spawnImpactRing(scene, cx, cy, 0x7e57c2, 70, { add: true, width: 5 });
  spawnImpactRing(scene, cx, cy, 0xd1c4e9, 50, { add: true, width: 3, duration: 450 });
  sparkleStars(scene, cx, cy, 0xe1bee7, 10, 60);
  flash(scene, 0x7e57c2, 80, 0.12);
  onImpact?.();
}

// --- Wizard M: Nova (2090) ---
function castNova(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;
  // Charge-up.
  const charge = scene.add.circle(cx, cy, 6, 0xff5722, 1).setDepth(24);
  additive(charge);
  scene.tweens.add({
    targets: charge, scale: { from: 0.5, to: 4 }, alpha: { from: 1, to: 0.6 },
    duration: 600, onComplete: () => charge.destroy(),
  });
  scene.time.delayedCall(500, () => {
    flash(scene, 0xff5722, 180, 0.3);
    for (let i = 0; i < 4; i++) {
      scene.time.delayedCall(i * 60, () => {
        spawnShockwave(scene, cx, cy, 0xff5722, 160 + i * 30, 600);
        spawnImpactRing(scene, cx, cy, 0xffc107, 140 + i * 20, { add: true, width: 5 });
      });
    }
    burst(scene, cx, cy, 0xff5722, 32, 180, 6, { add: true });
    burst(scene, cx, cy, 0xffd166, 20, 140, 4, { add: true });
    sparkleStars(scene, cx, cy, 0xffffff, 14, 80);
    shake(scene, 400, 0.018);
    onImpact?.();
  });
}

// --- Wizard M: Time Stop (2091) ---
function castTimeStop(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;
  // Expanding time-freeze ring.
  flash(scene, 0x81d4fa, 200, 0.2);
  for (let i = 0; i < 3; i++) {
    scene.time.delayedCall(i * 100, () => {
      spawnShockwave(scene, cx, cy, 0x81d4fa, 120 + i * 20, 600);
      spawnImpactRing(scene, cx, cy, 0xb3e5fc, 110 + i * 15, { add: true, width: 4 });
    });
  }
  // Clock-like rotating lines.
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI * 2 * i) / 12;
    const line = scene.add.rectangle(cx, cy, 2, 50, 0xb3e5fc, 0.7).setDepth(24).setRotation(a);
    additive(line);
    scene.tweens.add({
      targets: line, scaleY: 2, alpha: 0, rotation: a + Math.PI / 6,
      duration: 800, delay: i * 30,
      onComplete: () => line.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xe1f5fe, 12, 70);
  shake(scene, 200, 0.008);
  onImpact?.();
}

// --- Archer A: Triple Shot (3001) ---
function castTripleShot(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const baseAngle = Math.atan2(target.y - from.y, target.x - from.x);
  const spread = 0.18; // ~10 degrees between arrows
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(380, Math.max(130, dist * 1.2));

  for (let i = 0; i < 3; i++) {
    const angle = baseAngle + (i - 1) * spread;
    const tx = from.x + Math.cos(angle) * dist;
    const ty = from.y + Math.sin(angle) * dist;
    const arr = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(22);
    arr.setRotation(angle);
    const glow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(21)
      .setScale(1.3).setTint(0xfff59d).setAlpha(0.4).setRotation(angle);
    additive(glow);
    const trail = emitTrail(scene, arr, 0xfff59d, 24, 200, 0.9, 0.4);
    scene.tweens.add({
      targets: [arr, glow], x: tx, y: ty,
      delay: i * 50, duration: dur, ease: 'Linear',
      onComplete: () => {
        trail.remove(); arr.destroy(); glow.destroy();
        spawnImpactRing(scene, tx, ty, 0xffe082, 24, { add: true, width: 3 });
        spawnShockwave(scene, tx, ty, 0xfff59d, 20, 260);
        burst(scene, tx, ty, 0xfff59d, 8, 70, 3, { add: true });
        sparkleStars(scene, tx, ty, 0xffffff, 3, 18);
      },
    });
  }
  scene.time.delayedCall(dur * 0.6, () => { onImpact?.(); shake(scene, 60, 0.003); });
}

// --- Archer A: Bone Spirit (3003) ---
function castBoneSpirit(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const proj = scene.add.sprite(from.x, from.y, FX.spark).setDepth(22).setScale(1.4).setTint(0x7e57c2);
  additive(proj);
  proj.setRotation(angle);
  const trail = emitTrail(scene, proj, 0xb39ddb, 20, 300, 1.2, 0.55);
  // Homing wobble.
  const wobble = scene.time.addEvent({
    delay: 50, loop: true, callback: () => {
      const ghost = scene.add.circle(proj.x + Phaser.Math.Between(-4, 4), proj.y + Phaser.Math.Between(-4, 4), 3, 0xd1c4e9, 0.7).setDepth(21);
      additive(ghost);
      scene.tweens.add({ targets: ghost, alpha: 0, scale: 0.3, duration: 250, onComplete: () => ghost.destroy() });
    },
  });
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(500, Math.max(180, dist * 1.4));
  scene.tweens.add({
    targets: proj, x: target.x, y: target.y, duration: dur, ease: 'Sine.easeInOut',
    onComplete: () => {
      trail.remove(); wobble.remove(); proj.destroy();
      onImpact?.();
      flash(scene, 0x7e57c2, 70, 0.12);
      spawnShockwave(scene, target.x, target.y, 0x7e57c2, 44, 360);
      spawnImpactRing(scene, target.x, target.y, 0xb39ddb, 36, { add: true, width: 4 });
      burst(scene, target.x, target.y, 0xd1c4e9, 14, 100, 3, { add: true });
      sparkleStars(scene, target.x, target.y, 0xe1bee7, 6, 28);
      shake(scene, 80, 0.004);
    },
  });
}

// --- Archer A: One Shot One Kill (3004) ---
function castOneShotOneKill(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  // Charge-up glow at bow.
  const charge = scene.add.circle(from.x, from.y, 6, 0xffd166, 1).setDepth(24);
  additive(charge);
  scene.tweens.add({
    targets: charge, scale: { from: 0.5, to: 3 }, alpha: 0, duration: 400,
    onComplete: () => charge.destroy(),
  });
  scene.time.delayedCall(350, () => {
    // Super-fast arrow.
    const arr = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(22).setScale(1.5).setRotation(angle);
    const glow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(21).setScale(2.2).setTint(0xffd166).setAlpha(0.6).setRotation(angle);
    additive(glow);
    const trail = emitTrail(scene, arr, 0xffd166, 16, 300, 1.4, 0.6);
    const ex = target.x + Math.cos(angle) * 60;
    const ey = target.y + Math.sin(angle) * 60;
    scene.tweens.add({
      targets: [arr, glow], x: ex, y: ey, duration: 180, ease: 'Linear',
      onComplete: () => { trail.remove(); arr.destroy(); glow.destroy(); },
    });
    scene.time.delayedCall(100, () => {
      onImpact?.();
      flash(scene, 0xffd166, 100, 0.2);
      spawnShockwave(scene, target.x, target.y, 0xffd166, 60, 400);
      spawnImpactRing(scene, target.x, target.y, 0xffd166, 50, { add: true, width: 5 });
      spawnImpactRing(scene, target.x, target.y, 0xffffff, 30, { add: true, width: 3, duration: 320 });
      // Crit cross.
      const h = scene.add.rectangle(target.x, target.y, 60, 4, 0xffffff, 1).setDepth(25);
      const v = scene.add.rectangle(target.x, target.y, 4, 60, 0xffffff, 1).setDepth(25);
      additive(h); additive(v);
      scene.tweens.add({ targets: [h, v], scale: 2, alpha: 0, duration: 400, onComplete: () => { h.destroy(); v.destroy(); } });
      burst(scene, target.x, target.y, 0xffd166, 18, 120, 4, { add: true });
      sparkleStars(scene, target.x, target.y, 0xffffff, 8, 36);
      shake(scene, 140, 0.008);
    });
  });
}

// --- Archer B: Multi-Shot (3010) ---
function castMultiShot(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const baseAngle = Math.atan2(target.y - from.y, target.x - from.x);
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.max(200, dist * 1.2);

  for (let i = 0; i < 5; i++) {
    const angle = baseAngle + (i - 2) * 0.22;
    const tx = from.x + Math.cos(angle) * dist;
    const ty = from.y + Math.sin(angle) * dist;
    const arr = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(22);
    arr.setRotation(angle);
    const glow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(21)
      .setScale(1.3).setTint(0xffe082).setAlpha(0.4).setRotation(angle);
    additive(glow);
    const trail = emitTrail(scene, arr, 0xffe082, 26, 180, 0.8, 0.35);
    scene.tweens.add({
      targets: [arr, glow], x: tx, y: ty,
      delay: i * 30, duration: dur, ease: 'Linear',
      onComplete: () => {
        trail.remove(); arr.destroy(); glow.destroy();
        spawnImpactRing(scene, tx, ty, 0xffe082, 24, { add: true, width: 3 });
        spawnShockwave(scene, tx, ty, 0xfff59d, 20, 260);
        burst(scene, tx, ty, 0xfff59d, 8, 70, 3, { add: true });
        sparkleStars(scene, tx, ty, 0xffffff, 3, 18);
      },
    });
  }
  scene.time.delayedCall(dur * 0.6, () => { onImpact?.(); shake(scene, 60, 0.003); });
}

// --- Archer B: Ice Arrow (3011) ---
function castIceArrow(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  const proj = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(22).setTint(0x81d4fa);
  proj.setRotation(angle);
  const glow = scene.add.sprite(from.x, from.y, FX.arrow).setDepth(21).setScale(1.5).setTint(0xb3e5fc).setAlpha(0.5).setRotation(angle);
  additive(glow);
  const trail = emitTrail(scene, proj, 0x81d4fa, 22, 240, 1.0, 0.45);
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(400, Math.max(140, dist * 1.3));
  scene.tweens.add({
    targets: [proj, glow], x: target.x, y: target.y, duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove(); proj.destroy(); glow.destroy();
      onImpact?.();
      // Ice explosion.
      spawnShockwave(scene, target.x, target.y, 0x81d4fa, 50, 380);
      spawnImpactRing(scene, target.x, target.y, 0xb3e5fc, 44, { add: true, width: 4 });
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI * 2 * i) / 6;
        const shard = scene.add.rectangle(target.x, target.y, 4, 12, 0xb3e5fc, 1).setDepth(24).setRotation(a);
        additive(shard);
        scene.tweens.add({
          targets: shard, x: target.x + Math.cos(a) * 40, y: target.y + Math.sin(a) * 40,
          alpha: 0, duration: 400, onComplete: () => shard.destroy(),
        });
      }
      burst(scene, target.x, target.y, 0xb3e5fc, 12, 90, 3, { add: true });
      sparkleStars(scene, target.x, target.y, 0xe1f5fe, 5, 30);
      shake(scene, 80, 0.004);
    },
  });
}

// --- Archer B: Storm of Arrows (3013) ---
function castStormOfArrows(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;
  // Telegraph.
  const tele = scene.add.graphics({ x: cx, y: cy }).setDepth(20);
  tele.lineStyle(2, 0x27ae60, 0.6).strokeCircle(0, 0, 100);
  tele.fillStyle(0x27ae60, 0.08).fillCircle(0, 0, 100);
  scene.tweens.add({ targets: tele, alpha: 0, duration: 1400, onComplete: () => tele.destroy() });

  // 360-degree arrow storm.
  const N = 20;
  for (let i = 0; i < N; i++) {
    const a = (Math.PI * 2 * i) / N;
    const tx = cx + Math.cos(a) * Phaser.Math.Between(30, 90);
    const ty = cy + Math.sin(a) * Phaser.Math.Between(30, 90);
    const sx = tx - 50;
    const sy = ty - 220;
    scene.time.delayedCall(i * 30, () => {
      const arr = scene.add.sprite(sx, sy, FX.arrow).setDepth(22);
      arr.setRotation(Math.atan2(ty - sy, tx - sx));
      const trail = emitTrail(scene, arr, 0x27ae60, 26, 160, 0.8, 0.3);
      scene.tweens.add({
        targets: arr, x: tx, y: ty, duration: 300, ease: 'Quad.easeIn',
        onComplete: () => {
          trail.remove(); arr.destroy();
          spawnImpactRing(scene, tx, ty, 0x27ae60, 18, { add: true, width: 2 });
          burst(scene, tx, ty, 0xa5d6a7, 4, 50, 2, { add: true });
        },
      });
    });
  }
  scene.time.delayedCall(300, () => { onImpact?.(); shake(scene, 120, 0.005); });
  scene.time.delayedCall(N * 30 + 200, () => shake(scene, 140, 0.006));
}

// --- Archer C: Greater Defense (3021) ---
function castGreaterDefense(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  const sh = scene.add.sprite(cx, cy, FX.shield).setDepth(22).setAlpha(0).setScale(0.4);
  sh.setTint(0x27ae60);
  additive(sh);
  scene.tweens.add({
    targets: sh, alpha: { from: 0, to: 0.9 }, scale: 1.3,
    duration: 240, yoyo: true, hold: 600,
    onComplete: () => sh.destroy(),
  });
  spawnImpactRing(scene, cx, cy, 0x27ae60, 55, { add: true, width: 4 });
  spawnImpactRing(scene, cx, cy, 0xc8e6c9, 38, { add: true, width: 2, duration: 400 });
  sparkleStars(scene, cx, cy, 0xa5d6a7, 8, 46);
  onImpact?.();
}

// --- Archer C: Greater Damage (3022) ---
function castGreaterDamage(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  // Red-orange power-up aura.
  spawnImpactRing(scene, cx, cy, 0xff5722, 55, { add: true, width: 4 });
  spawnImpactRing(scene, cx, cy, 0xffc107, 38, { add: true, width: 2, duration: 400 });
  spawnShockwave(scene, cx, cy, 0xff5722, 45, 380);
  for (let i = 0; i < 10; i++) {
    const ox = Phaser.Math.Between(-24, 24);
    const m = scene.add.circle(cx + ox, cy + 14, 3, 0xff5722, 1).setDepth(23);
    additive(m);
    scene.tweens.add({
      targets: m, y: cy - 36 - Math.random() * 20, alpha: 0,
      delay: i * 24, duration: 650, ease: 'Cubic.easeOut',
      onComplete: () => m.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xffd166, 6, 40);
  flash(scene, 0xff5722, 70, 0.1);
  onImpact?.();
}

// --- Archer C: Summon Goddess (3023) ---
function castSummonGoddess(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? target.x;
  const cy = caster?.y ?? target.y;
  // Holy light beam.
  const beam = scene.add.rectangle(cx, cy - 160, 16, 320, 0xffd166, 0).setDepth(24);
  additive(beam);
  scene.tweens.add({
    targets: beam, alpha: { from: 0, to: 0.7 }, scaleX: 2.5,
    duration: 350, yoyo: true, hold: 600,
    onComplete: () => beam.destroy(),
  });
  // Holy rings expanding.
  for (let i = 0; i < 3; i++) {
    scene.time.delayedCall(i * 120, () => {
      spawnImpactRing(scene, cx, cy, 0xffd166, 80 + i * 15, { add: true, width: 4 });
      spawnShockwave(scene, cx, cy, 0xfff9c4, 60 + i * 10, 500);
    });
  }
  // Heal sparkles in AoE.
  for (let i = 0; i < 16; i++) {
    const ox = Phaser.Math.Between(-70, 70);
    const oy = Phaser.Math.Between(-50, 50);
    const sp = scene.add.circle(cx + ox, cy + oy + 10, 3, 0x66bb6a, 1).setDepth(23);
    additive(sp);
    scene.tweens.add({
      targets: sp, y: sp.y - 40 - Math.random() * 20, alpha: 0,
      delay: 200 + i * 30, duration: 700, ease: 'Cubic.easeOut',
      onComplete: () => sp.destroy(),
    });
  }
  sparkleStars(scene, cx, cy, 0xffffff, 12, 60);
  flash(scene, 0xffd166, 120, 0.18);
  shake(scene, 100, 0.004);
  onImpact?.();
}

// --- Archer M: Phoenix Arrow (3090) ---
function castPhoenixArrow(scene: Phaser.Scene, { caster, target, onImpact }: SkillFxOpts) {
  const from = caster ?? target;
  const angle = Math.atan2(target.y - from.y, target.x - from.x);
  // Flaming phoenix projectile.
  const proj = scene.add.sprite(from.x, from.y, FX.fireball).setDepth(22).setScale(1.5).setRotation(angle);
  additive(proj);
  const trail = emitTrail(scene, proj, 0xff5722, 16, 400, 1.5, 0.6);
  const embers = scene.time.addEvent({
    delay: 25, loop: true, callback: () => {
      const e = scene.add.circle(proj.x + Phaser.Math.Between(-8, 8), proj.y + Phaser.Math.Between(-8, 8),
        Phaser.Math.Between(3, 7), 0xffd166, 0.9).setDepth(21);
      additive(e);
      scene.tweens.add({ targets: e, alpha: 0, scale: 0.2, duration: 350, onComplete: () => e.destroy() });
    },
  });
  const dist = Phaser.Math.Distance.Between(from.x, from.y, target.x, target.y);
  const dur = Math.min(500, Math.max(180, dist * 1.2));
  scene.tweens.add({
    targets: proj, x: target.x, y: target.y, duration: dur, ease: 'Linear',
    onComplete: () => {
      trail.remove(); embers.remove(); proj.destroy();
      onImpact?.();
      flash(scene, 0xff5722, 150, 0.28);
      for (let i = 0; i < 3; i++) {
        scene.time.delayedCall(i * 70, () => {
          spawnShockwave(scene, target.x, target.y, 0xff5722, 130 + i * 20, 550);
          spawnImpactRing(scene, target.x, target.y, 0xffc107, 110 + i * 15, { add: true, width: 5 });
        });
      }
      burst(scene, target.x, target.y, 0xff5722, 30, 170, 5, { add: true });
      burst(scene, target.x, target.y, 0xffd166, 18, 130, 4, { add: true });
      sparkleStars(scene, target.x, target.y, 0xffffff, 12, 60);
      shake(scene, 300, 0.014);
    },
  });
}

// --- Archer M: Hawk Eye (3091) ---
function castHawkEye(scene: Phaser.Scene, { caster, onImpact }: SkillFxOpts) {
  const cx = caster?.x ?? 0;
  const cy = caster?.y ?? 0;
  // Targeting reticle.
  const h = scene.add.rectangle(cx, cy, 60, 2, 0xffd166, 0).setDepth(24);
  const v = scene.add.rectangle(cx, cy, 2, 60, 0xffd166, 0).setDepth(24);
  additive(h); additive(v);
  scene.tweens.add({
    targets: [h, v], alpha: { from: 0, to: 0.9 }, scale: 1.5,
    duration: 300, yoyo: true, hold: 500,
    onComplete: () => { h.destroy(); v.destroy(); },
  });
  // Scope circle.
  spawnImpactRing(scene, cx, cy, 0xffd166, 50, { add: true, width: 3 });
  spawnImpactRing(scene, cx, cy, 0xffffff, 35, { add: true, width: 2, duration: 400 });
  spawnShockwave(scene, cx, cy, 0xffd166, 40, 400);
  sparkleStars(scene, cx, cy, 0xffd166, 8, 44);
  flash(scene, 0xffd166, 80, 0.1);
  onImpact?.();
}
